import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StatusChip } from '@/components/status/StatusChip';
import type { WhitelabelDnsRecord } from '@/api/saasAuthTypes';
import { useToast } from '@/components/useToast';

export interface RecordsTableProps {
  records: WhitelabelDnsRecord[];
  disabled?: boolean;
  verifying?: boolean;
  onVerify: () => Promise<void>;
}

function recordsText(records: WhitelabelDnsRecord[]): string {
  return records.map((row) => `${row.host}\t${row.type || 'CNAME'}\t${row.target}`).join('\n');
}

export function RecordsTable({ records, disabled, verifying, onVerify }: RecordsTableProps) {
  const { t } = useTranslation();
  const showToast = useToast();

  const handleCopy = async () => {
    const clipboard = navigator.clipboard;
    if (clipboard === undefined || typeof clipboard.writeText !== 'function') return;
    await clipboard.writeText(recordsText(records));
    showToast(t('domain.records.copied'));
  };

  // A real table (TMMRSL-0072), named, the host as each row's header; it keeps the grid's look:
  // the column proportions, the tinted overline header and the mono values.
  // With a fixed layout a long host or target wraps inside its column rather than spilling over.
  const cell = {
    px: 1.5,
    py: 1.25,
    borderBottom: 1,
    borderColor: 'divider',
    overflowWrap: 'anywhere',
  } as const;
  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ overflow: 'auto' }}>
        <Table
          aria-label={t('domain.records.label')}
          sx={{ minWidth: 640, tableLayout: 'fixed', typography: 'body2' }}
        >
          <colgroup>
            <col style={{ width: '33%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '34%' }} />
            <col style={{ width: '19%' }} />
          </colgroup>
          <TableHead>
            <TableRow>
              {[
                t('domain.records.host'),
                t('domain.records.type'),
                t('domain.records.target'),
                t('domain.records.state'),
              ].map((label) => (
                <TableCell
                  key={label}
                  sx={{ ...cell, py: 1, bgcolor: 'action.hover', typography: 'overline' }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((row) => (
              <TableRow key={`${row.host}-${row.target}`}>
                <TableCell component="th" scope="row" sx={{ ...cell, typography: 'mono' }}>
                  {row.host}
                </TableCell>
                <TableCell sx={{ ...cell, typography: 'mono' }}>{row.type || 'CNAME'}</TableCell>
                <TableCell sx={{ ...cell, typography: 'mono', color: 'text.secondary' }}>
                  {row.target}
                </TableCell>
                <TableCell sx={cell}>
                  <StatusChip domain="dns" status={row.dnsVerified ? 'VERIFIED' : 'PENDING'} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        {disabled ? null : (
          <>
            <Button variant="contained" onClick={() => void onVerify()} disabled={verifying}>
              {t('domain.records.recheck')}
            </Button>
            <Button variant="outlined" onClick={() => void handleCopy()} disabled={records.length === 0}>
              {t('domain.records.copyAll')}
            </Button>
          </>
        )}
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('domain.records.checkNote')}
        </Typography>
      </Box>
    </Stack>
  );
}
